import { Card, Icon, IconButton, Tooltip } from "@mui/material";
import SuiBox from "components/SuiBox";
import SuiTypography from "components/SuiTypography";
import Alert from "custom/Alert";
import DashboardLayout from "custom/LayoutContainers/DashboardLayout";
import DashboardNavbar from "custom/Navbars/DashboardNavbar";
import DataTable from "custom/Tables/DataTable";
import FirstPageViewer from "components/PdfViewer";
import { useState, useMemo, useEffect } from "react";
import PropTypes from "prop-types";
import SuiInput from "components/SuiInput";
import { connect } from "react-redux";
import {
  getAllSoundMaterials as getAllSoundMaterialsAction,
  deleteSoundMaterial as deleteSoundMaterialAction,
} from "redux/actions/pages/sound-material";
import Swal from "sweetalert2";

// PropTypes for the Cell components
const PdfCell = ({ value }) => (
  <SuiBox sx={{ width: "100px", height: "140px" }}>
    <FirstPageViewer fileUrl={value} />
  </SuiBox>
);

PdfCell.propTypes = {
  value: PropTypes.string.isRequired,
};

const ActionsCell = ({ row, onDownload, onDelete }) => (
  <SuiBox display="flex" gap={1}>
    <Tooltip title="İndir">
      <IconButton color="info" onClick={() => onDownload(row.original.fileUrl)}>
        <Icon>download</Icon>
      </IconButton>
    </Tooltip>
    <Tooltip title="Sil">
      <IconButton color="error" onClick={() => onDelete(row.original.id)}>
        <Icon>delete</Icon>
      </IconButton>
    </Tooltip>
  </SuiBox>
);

ActionsCell.propTypes = {
  row: PropTypes.shape({
    original: PropTypes.shape({
      fileUrl: PropTypes.string.isRequired,
      id: PropTypes.number.isRequired,
    }).isRequired,
  }).isRequired,
  onDownload: PropTypes.func.isRequired,
  onDelete: PropTypes.func.isRequired,
};

function MineSoundMaterials({
  getAllSoundMaterials,
  deleteSoundMaterial,
  mineSoundMaterials,
  loading,
}) {
  // Search state
  const [searchTerm, setSearchTerm] = useState("");

  // Component mount olduğunda veri çek
  useEffect(() => {
    getAllSoundMaterials();
  }, [getAllSoundMaterials]);

  // Filtered materials based on search term
  const filteredMaterials = useMemo(() => {
    // API'den gelen veri doğrudan bir dizi olabilir
    if (Array.isArray(mineSoundMaterials)) {
      const materials = mineSoundMaterials.map((item) => ({
        id: item.id,
        name: item.name,
        fileUrl: item.file,
        letters: item.letter,
        createdAt: item.created_at
          ? new Date(item.created_at).toISOString().split("T")[0]
          : new Date().toISOString().split("T")[0],
        createdBy: item.created_by || "Bilinmiyor",
      }));

      if (!searchTerm) return materials;

      const lowerSearchTerm = searchTerm.toLowerCase();
      return materials.filter(
        (material) =>
          material.name.toLowerCase().includes(lowerSearchTerm) ||
          material.letters.toLowerCase().includes(lowerSearchTerm) ||
          material.createdAt.includes(lowerSearchTerm)
      );
    }

    // API'den gelen veri bir nesne içinde rows olarak gelebilir
    if (!mineSoundMaterials || !mineSoundMaterials.rows) return [];

    const materials = mineSoundMaterials.rows.map((item) => ({
      id: item.id,
      name: item.name,
      fileUrl: item.file,
      letters: item.letter,
      createdAt: item.created_at
        ? new Date(item.created_at).toISOString().split("T")[0]
        : new Date().toISOString().split("T")[0],
    }));

    if (!searchTerm) return materials;

    const lowerSearchTerm = searchTerm.toLowerCase();
    return materials.filter(
      (material) =>
        material.name.toLowerCase().includes(lowerSearchTerm) ||
        material.letters.toLowerCase().includes(lowerSearchTerm) ||
        material.createdAt.includes(lowerSearchTerm)
    );
  }, [mineSoundMaterials, searchTerm]);

  // Handle delete material
  const handleDelete = (id) => {
    const newSwal = Swal.mixin({
      customClass: {
        confirmButton: "button button-info",
        cancelButton: "button button-error",
      },
      buttonsStyling: false,
    });

    newSwal
      .fire({
        title: "Emin misiniz?",
        text: "Bu ses materyalini silmek istediginizden emin misiniz?",
        icon: "warning",
        showCancelButton: true,
        confirmButtonColor: "#3085d6",
        cancelButtonColor: "#d33",
        confirmButtonText: "Evet, SİL!",
        cancelButtonText: "İptal",
      })
      .then((result) => {
        if (result.isConfirmed) {
          Swal.fire("Silindi!", "Ses materyali basariyla silindi.", "success");
          deleteSoundMaterial(id);

          setTimeout(() => {
            getAllSoundMaterials();
          }, 1000);
        } else if (result.dismiss === Swal.DismissReason.cancel) {
          Swal.fire("İşlem iptal edildi", "Ses materyali silinmedi", "error");
        }
      });
  };

  const handleDownload = (fileUrl) => {
    window.open(fileUrl, "_blank");
  };

  // Table columns configuration
  const columns = [
    {
      Header: "Materyal",
      accessor: "fileUrl",
      Cell: PdfCell,
    },
    {
      Header: "Materyal Adı",
      accessor: "name",
    },
    {
      Header: "İçerdiği Ses(ler)",
      accessor: "letters",
    },
    {
      Header: "Oluşturan",
      accessor: "createdBy",
    },
    {
      Header: "Oluşturulma Tarihi",
      accessor: "createdAt",
    },
    {
      Header: "İşlemler",
      accessor: "actions",
      Cell: (props) => (
        <ActionsCell {...props} onDownload={handleDownload} onDelete={handleDelete} />
      ),
    },
  ];

  return (
    <>
      <Alert />
      <DashboardLayout>
        <DashboardNavbar />
        <SuiBox mt={3} mb={4}>
          <Card>
            <SuiBox p={3}>
              <SuiBox display="flex" justifyContent="space-between" alignItems="center" mb={3}>
                <SuiTypography variant="h5" fontWeight="medium">
                  Materyallerim
                </SuiTypography>
                <SuiBox width="12rem">
                  <SuiInput
                    placeholder="Ara..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                  />
                </SuiBox>
              </SuiBox>
              <DataTable
                table={{
                  columns,
                  rows: filteredMaterials,
                }}
                entriesPerPage
                showTotalEntries
                pagination={{ variant: "gradient", color: "info" }}
                isSorted
                canSearch={false}
                isLoading={loading}
              />
            </SuiBox>
          </Card>
        </SuiBox>
      </DashboardLayout>
    </>
  );
}

MineSoundMaterials.propTypes = {
  getAllSoundMaterials: PropTypes.func.isRequired,
  deleteSoundMaterial: PropTypes.func.isRequired,
  mineSoundMaterials: PropTypes.oneOfType([
    PropTypes.shape({
      success: PropTypes.bool,
      rows: PropTypes.arrayOf(
        PropTypes.shape({
          id: PropTypes.number,
          letter: PropTypes.string,
          name: PropTypes.string,
          file: PropTypes.string,
          is_active: PropTypes.bool,
          sound_order: PropTypes.number,
        })
      ),
    }),
    PropTypes.arrayOf(
      PropTypes.shape({
        id: PropTypes.number,
        letter: PropTypes.string,
        name: PropTypes.string,
        file: PropTypes.string,
        is_active: PropTypes.bool,
        sound_order: PropTypes.number,
      })
    ),
  ]),
  loading: PropTypes.bool,
};

MineSoundMaterials.defaultProps = {
  mineSoundMaterials: { success: false, rows: [] },
  loading: false,
};

const mapStateToProps = (state) => ({
  mineSoundMaterials: state.data.mineSoundMaterials,
  loading: state.ui.loading[`${state.ui.feature}`] || false,
});

const mapDispatchToProps = {
  getAllSoundMaterials: getAllSoundMaterialsAction,
  deleteSoundMaterial: deleteSoundMaterialAction,
};

export default connect(mapStateToProps, mapDispatchToProps)(MineSoundMaterials);
